import { useState } from "react";

export interface CastLook {
  id: string;
  name: string;
  thumbnail: string;
}
export interface CastOption {
  id: string;
  name: string;
  thumbnail: string;
  looks: CastLook[];
}
export interface CastPick {
  lead: string;
  supporting: string[];
  /** Chosen saved look per person; no entry means Iris decides. */
  looks: Record<string, string>;
}

/**
 * The maker's cast as portraits right in the conversation. The first person picked leads;
 * anyone can be made the lead with a tap. For each person a saved look can be chosen.
 * `mode` wardrobe shows only the looks of the people already in the film.
 */
export function CastPicker({
  options,
  mode,
  initial,
  disabled,
  onPick,
}: {
  options: CastOption[];
  mode: "cast" | "wardrobe";
  initial?: CastPick;
  disabled?: boolean;
  onPick: (pick: CastPick) => void;
}) {
  const [lead, setLead] = useState(initial?.lead || "");
  const [supporting, setSupporting] = useState<string[]>(initial?.supporting || []);
  const [looks, setLooks] = useState<Record<string, string>>(initial?.looks || {});
  const chosen = [lead, ...supporting].filter(Boolean);
  const people = mode === "wardrobe" ? options.filter((option) => chosen.includes(option.id)) : options;

  const toggle = (id: string) => {
    if (id === lead) {
      const [next, ...rest] = supporting;
      setLead(next || "");
      setSupporting(rest);
    } else if (supporting.includes(id)) setSupporting(supporting.filter((item) => item !== id));
    else if (!lead) setLead(id);
    else setSupporting([...supporting, id]);
  };
  const makeLead = (id: string) => {
    setSupporting([...(lead ? [lead] : []), ...supporting.filter((item) => item !== id)]);
    setLead(id);
  };

  return (
    <div className="cast-picker" role="group" aria-label={mode === "cast" ? "Choose your cast" : "Choose their looks"}>
      {mode === "cast" && (
        <div className="cast-picker-portraits">
          {people.map((person) => {
            const role = person.id === lead ? "Lead" : supporting.includes(person.id) ? "In the film" : "";
            return (
              <button
                key={person.id}
                type="button"
                className={`cast-picker-portrait${role ? " picked" : ""}`}
                aria-pressed={!!role}
                disabled={disabled}
                onClick={() => toggle(person.id)}
              >
                <span className="cast-picker-photo">
                  {person.thumbnail ? (
                    <img src={person.thumbnail} alt="" loading="lazy" />
                  ) : (
                    <span aria-hidden="true">{person.name.slice(0, 1)}</span>
                  )}
                </span>
                <b>{person.name}</b>
                {role && <small>{role}</small>}
              </button>
            );
          })}
        </div>
      )}
      {chosen
        .map((id) => options.find((option) => option.id === id))
        .filter((person): person is CastOption => !!person && person.looks.length > 0)
        .map((person) => (
          <div key={person.id} className="cast-picker-looks">
            <div className="cast-picker-looks-head">
              <b>{person.name}</b>
              {person.id !== lead && mode === "cast" && (
                <button type="button" className="cast-picker-make-lead" disabled={disabled} onClick={() => makeLead(person.id)}>
                  Make lead
                </button>
              )}
            </div>
            <div className="cast-picker-look-row">
              <button
                type="button"
                className={`cast-picker-look${!looks[person.id] ? " picked" : ""}`}
                aria-pressed={!looks[person.id]}
                disabled={disabled}
                onClick={() => setLooks(({ [person.id]: _, ...rest }) => rest)}
              >
                <span className="cast-picker-look-photo iris-choice">Iris decides</span>
              </button>
              {person.looks.map((look) => (
                <button
                  key={look.id}
                  type="button"
                  className={`cast-picker-look${looks[person.id] === look.id ? " picked" : ""}`}
                  aria-pressed={looks[person.id] === look.id}
                  aria-label={`${person.name} wears ${look.name}`}
                  disabled={disabled}
                  onClick={() => setLooks({ ...looks, [person.id]: look.id })}
                >
                  <span className="cast-picker-look-photo">
                    {look.thumbnail ? <img src={look.thumbnail} alt="" loading="lazy" /> : null}
                  </span>
                  <small>{look.name}</small>
                </button>
              ))}
            </div>
          </div>
        ))}
      <button
        type="button"
        className="cast-picker-confirm"
        disabled={disabled || !lead}
        onClick={() => onPick({ lead, supporting, looks })}
      >
        {mode === "cast" ? "Use this cast" : "Use these looks"}
      </button>
    </div>
  );
}
